import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

/** Preset windows offered in the dropdown. The backend stores any hour value
 * (per-VIP overrides can be set via the API), so a non-preset resolved value
 * is folded in as an extra option rather than being lost. */
const NOTICE_PRESETS_HOURS = [0, 2, 12, 24, 48, 72] as const;

function noticeLabel(hours: number): string {
  if (hours === 0) return 'Any time up to the appointment';
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days === 1 ? '' : 's'} before`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'} before`;
}

export interface ManagePolicyViewProps {
  minimumNoticeHours: number;
  cancelEnabled: boolean;
  /** Subject wording (e.g. "patients"/"clients") from the owner's preference. */
  subjectPlural?: string;
  isLoading?: boolean;
  disabled?: boolean;
  onNoticeChange: (hours: number) => void;
  onCancelEnabledChange: (enabled: boolean) => void;
}

/** Presentational settings for the patient self-serve manage policy (GDN-2272):
 * how close to the appointment a booking can still be changed, and whether
 * self-cancel is allowed at all. Org-wide only; per-encounter-type overrides
 * and the self-reschedule flag live in the API for now. Selects save
 * immediately. */
export function ManagePolicyView({
  minimumNoticeHours,
  cancelEnabled,
  subjectPlural = 'patients',
  isLoading = false,
  disabled = false,
  onNoticeChange,
  onCancelEnabledChange,
}: ManagePolicyViewProps) {
  const options = Array.from(
    new Set<number>([...NOTICE_PRESETS_HOURS, minimumNoticeHours]),
  ).sort((a, b) => a - b);

  const handleNoticeChange = (event: SelectChangeEvent<number>) => {
    onNoticeChange(event.target.value);
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Rescheduling &amp; cancellation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Control how {subjectPlural} manage their own appointments from the
          link in their reminders.
        </Typography>
      </Box>

      {isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Cut-off for changes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {subjectPlural} can reschedule or cancel themselves up to this
                far ahead. Closer to the start, they&apos;re told to call.
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 220 }} size="small" disabled={disabled}>
              <Select<number>
                value={minimumNoticeHours}
                onChange={handleNoticeChange}
                inputProps={{ 'aria-label': 'Cut-off for self-serve changes' }}
              >
                {options.map(hours => (
                  <MenuItem key={hours} value={hours}>
                    {noticeLabel(hours)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControlLabel
            sx={{ m: 0, alignItems: 'flex-start' }}
            control={
              <Switch
                checked={cancelEnabled}
                disabled={disabled}
                onChange={(_e, checked) => onCancelEnabledChange(checked)}
                sx={{ mt: -0.5 }}
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Allow {subjectPlural} to cancel
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cancelEnabled
                    ? `${subjectPlural} can cancel within the cut-off above.`
                    : `Cancelling is off — ${subjectPlural} must call the practice to cancel.`}
                </Typography>
              </Box>
            }
          />
        </Stack>
      )}
    </Paper>
  );
}
